import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { Header } from '@/components/Header';
import { FlowNavigation } from '@/components/FlowNavigation';
import ShuttleIcon from '@/assets/icons/airport-shuttle-white.svg?react';
import { TimeOptionGrid, type TimeOptionItem } from '@/components/TimeOptionGrid';
import './BusReservation.scss';

interface BusSchedule {
    scheduleId: number;
    departureTime: string;
    totalSeats: number;
    reservedSeats: number;
    remainingSeats: number;
    isPast: boolean;
}

interface Bus {
    busId: number;
    busName: string;
    plateNumber: string;
    totalSeats: number;
    schedules: BusSchedule[];
}

export function BusReservation() {
    const navigate = useNavigate();
    const { isMobile } = useDevice();

    const [buses, setBuses] = useState<Bus[]>([]);
    const [selectedBusId, setSelectedBusId] = useState<number | null>(null);
    const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetch('/api/center/buses')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch buses');
                return res.json();
            })
            .then((data: Bus[]) => {
                setBuses(data);
                if (data.length > 0) {
                    setSelectedBusId(data[0].busId);
                }
            })
            .catch((err) => {
                console.error('Error fetching buses:', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handlePrev = () => {
        navigate('/selection');
    };

    const handleNext = async () => {
        if (!selectedScheduleId) return;

        try {
            const response = await fetch('/api/center/buses/reservations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    busScheduleId: selectedScheduleId,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                navigate('/bus/complete', {
                    state: {
                        reservationData: data
                    }
                });
            } else {
                const errorData = await response.json().catch(() => ({}));
                const message = errorData.message || '예약에 실패했습니다.';
                alert(message);
            }
        } catch (error) {
            console.error('Reservation failed:', error);
            alert('예약 중 오류가 발생했습니다.');
        }
    };

    // 현재 선택된 버스의 스케줄 목록
    const currentBus = buses.find(b => b.busId === selectedBusId);
    const schedules = currentBus?.schedules || [];
    const timeOptions: TimeOptionItem[] = schedules.map((slot) => {
        const isSoldOut = slot.remainingSeats <= 0;
        const formattedTime = slot.departureTime.substring(0, 5);

        return {
            id: slot.scheduleId,
            disabled: isSoldOut || slot.isPast,
            content: (
                <>
                    {formattedTime} 출발{isSoldOut ? ` (만석)` : ` (${slot.remainingSeats}석 남음)`}
                </>
            ),
        };
    });

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading buses...</div>;
    }

    return (
        <div className={`bus-reservation-page ${isMobile ? 'bus-reservation-page--mobile' : ''}`}>
            <Header />

            <main className="bus-reservation-main">
                <div className="bus-main-top">
                    <h1 className="page-title">별빛버스 예약</h1>
                    <p className="page-subtitle">탑승할 차량과 시간을 선택해주세요</p>
                </div>

                {buses.length === 0 ? (
                    <div className="no-data-message">운행 중인 버스가 없습니다.</div>
                ) : (
                    <>
                        <div className="bus-selection-tabs">
                            {buses.map((bus) => (
                                <button
                                    key={bus.busId}
                                    className={`bus-tab ${selectedBusId === bus.busId ? 'bus-tab--active' : ''}`}
                                    onClick={() => {
                                        setSelectedBusId(bus.busId);
                                        setSelectedScheduleId(null); // 버스 변경 시 스케줄 초기화
                                    }}
                                >
                                    <ShuttleIcon className="bus-icon-svg" />
                                    <span className="bus-name">{bus.busName} ({bus.plateNumber}, {bus.totalSeats}석)</span>
                                </button>
                            ))}
                        </div>

                        <div className="time-selection-container">
                            <TimeOptionGrid
                                items={timeOptions}
                                selectedId={selectedScheduleId}
                                onSelect={setSelectedScheduleId}
                                emptyText="해당 버스의 운행 스케줄이 없습니다."
                            />
                        </div>
                    </>
                )}
            </main>

            <FlowNavigation
                onPrev={handlePrev}
                onNext={handleNext}
                nextLabel="예약"
                isNextDisabled={!selectedScheduleId}
                hideNextArrow={true}
            />
        </div>
    );
}

export default BusReservation;
